import { useState, useCallback } from 'react'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Search, Filter, FolderOpen, RefreshCw } from 'lucide-react'
import { ClienteCombobox } from '@/components/ui/cliente-combobox'
import { DocumentoCard } from '@/components/documentos/DocumentoCard'
import { SubirDocumentoDialog } from '@/components/documentos/SubirDocumentoDialog'
import { EditarDocumentoDialog } from '@/components/documentos/EditarDocumentoDialog'
import { DocumentoPreviewDialog } from '@/components/documentos/DocumentoPreviewDialog'
import { DocumentosPorVencer } from '@/components/documentos/DocumentosPorVencer'
import { DocumentosExplorer } from '@/components/documentos/DocumentosExplorer'
import {
  useDocumentos,
  useEliminarDocumento,
  useDescargarDocumento,
} from '@/hooks/useDocumentos'
import { useClientes } from '@/hooks/useClientes'
import { usePermissions } from '@/hooks/usePermissions'
import { toast } from 'sonner'
import { handleMutationError } from '@/lib/permisos-guard'
import type { Documento, CategoriaDocumento } from '@/types/database'
import { getCategoriasOptions } from '@/types/documentos'

// Usar solo categorías del workspace contable (excluye jurídicas)
const categoriaOptions = [
  { value: 'todas', label: 'Todas las categorías' },
  ...getCategoriasOptions('contable'),
]

export function Documentos() {
  const { puede } = usePermissions()
  const [busqueda, setBusqueda] = useState('')
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('todas')
  const [clienteFiltro, setClienteFiltro] = useState<string>('todos')
  const [dialogSubirOpen, setDialogSubirOpen] = useState(false)
  const [dialogEditarOpen, setDialogEditarOpen] = useState(false)
  const [dialogPreviewOpen, setDialogPreviewOpen] = useState(false)
  const [documentoEditar, setDocumentoEditar] = useState<Documento | null>(null)
  const [documentoPreview, setDocumentoPreview] = useState<Documento | null>(null)

  // Obtener lista de clientes para el filtro
  const { data: clientes = [] } = useClientes()

  // Obtener documentos con filtros (solo cuando no hay cliente seleccionado)
  const {
    data: documentos = [],
    isLoading,
    refetch,
  } = useDocumentos({
    cliente_id: clienteFiltro !== 'todos' ? clienteFiltro : undefined,
    categoria: categoriaFiltro !== 'todas' ? (categoriaFiltro as CategoriaDocumento) : undefined,
    busqueda: busqueda || undefined,
  })

  const eliminarDocumento = useEliminarDocumento()
  const descargarDocumento = useDescargarDocumento()

  // Filtrar por búsqueda local (adicional al filtro del servidor)
  const documentosFiltrados = documentos.filter((doc) => {
    if (!busqueda) return true
    const busquedaLower = busqueda.toLowerCase()
    return (
      doc.nombre.toLowerCase().includes(busquedaLower) ||
      doc.nombre_original.toLowerCase().includes(busquedaLower)
    )
  })

  const handleVer = useCallback((documento: Documento) => {
    setDocumentoPreview(documento)
    setDialogPreviewOpen(true)
  }, [])

  const handleDescargar = useCallback(
    async (documento: Documento) => {
      try {
        await descargarDocumento.mutateAsync({
          path: documento.archivo_path,
          nombreArchivo: documento.nombre_original,
        })
        toast.success('Descarga iniciada')
      } catch (error) {
        console.error('Error al descargar:', error)
        toast.error('Error al descargar el documento')
      }
    },
    [descargarDocumento]
  )

  const handleEditar = useCallback((documento: Documento) => {
    setDocumentoEditar(documento)
    setDialogEditarOpen(true)
  }, [])

  const handleEliminar = useCallback(
    async (documento: Documento) => {
      try {
        await eliminarDocumento.mutateAsync(documento.id)
        toast.success('Documento eliminado')
      } catch (error) {
        handleMutationError(error, 'Error al eliminar el documento')
      }
    },
    [eliminarDocumento]
  )

  // Obtener el cliente seleccionado
  const clienteSeleccionado = clientes.find((c) => c.id === clienteFiltro)
  const tieneClienteSeleccionado = clienteFiltro !== 'todos'

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Documentos"
        description="Gestiona los documentos de tus clientes"
      />

      <div className="flex-1 p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Selector de cliente con búsqueda */}
        <div className="flex flex-col sm:flex-row gap-3 justify-between">
          <ClienteCombobox
            clientes={clientes}
            value={clienteFiltro}
            onValueChange={setClienteFiltro}
            placeholder="Selecciona un cliente"
            showAllOption={{ value: 'todos', label: 'Todos los clientes' }}
            className="w-full sm:w-[280px]"
          />

          {/* Botón de subir solo visible cuando no hay cliente (vista general) */}
          {!tieneClienteSeleccionado && puede.subirDocumento && (
            <Button onClick={() => setDialogSubirOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Subir Documento
            </Button>
          )}
        </div>

        {/* Contenido condicional */}
        {tieneClienteSeleccionado ? (
          /* Vista de explorador de carpetas cuando hay cliente seleccionado */
          <>
            {/* Documentos por vencer - above on mobile */}
            <div className="lg:hidden">
              <DocumentosPorVencer />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 flex-1">
              <div className="lg:col-span-3 min-h-[300px] md:min-h-[500px]">
                <DocumentosExplorer
                  clienteId={clienteFiltro}
                  clienteNombre={clienteSeleccionado?.nombre ?? 'Cliente'}
                />
              </div>
              <div className="hidden lg:block lg:col-span-1">
                <DocumentosPorVencer />
              </div>
            </div>
          </>
        ) : (
          /* Vista de lista plana cuando no hay cliente seleccionado */
          <>
            {/* Filtros adicionales */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar documentos..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select value={categoriaFiltro} onValueChange={setCategoriaFiltro}>
                <SelectTrigger className="w-full sm:w-[220px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categoriaOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            {/* Documentos por vencer - banner on mobile, sidebar on desktop */}
            <div className="lg:hidden">
              <DocumentosPorVencer />
            </div>

            {/* Contenido con sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Contenido principal */}
              <div className="lg:col-span-3 space-y-4">
                {/* Mensaje informativo */}
                <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                  Selecciona un cliente para ver sus carpetas de documentos organizadas por categoría
                </div>

                {/* Lista de documentos */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : documentosFiltrados.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <FolderOpen className="h-16 w-16 mb-4 opacity-50" />
                    {busqueda || categoriaFiltro !== 'todas' ? (
                      <>
                        <p className="font-medium text-lg">No se encontraron documentos</p>
                        <p className="text-sm">Intenta con otros filtros de búsqueda</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-lg">No hay documentos</p>
                        <p className="text-sm">
                          Selecciona un cliente para ver y subir documentos
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {documentosFiltrados.length} documento
                      {documentosFiltrados.length !== 1 ? 's' : ''} encontrado
                      {documentosFiltrados.length !== 1 ? 's' : ''}
                    </p>
                    {documentosFiltrados.map((documento) => (
                      <DocumentoCard
                        key={documento.id}
                        documento={documento}
                        onVer={handleVer}
                        onDescargar={handleDescargar}
                        onEditar={puede.editarDocumento ? handleEditar : undefined}
                        onEliminar={puede.eliminarDocumento ? handleEliminar : undefined}
                        showCliente={true}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Sidebar - Documentos por vencer (desktop only, mobile version is above) */}
              <div className="hidden lg:block lg:col-span-1">
                <DocumentosPorVencer />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Dialogs (solo para vista general) */}
      <SubirDocumentoDialog
        open={dialogSubirOpen}
        onOpenChange={setDialogSubirOpen}
        onSuccess={() => refetch()}
      />

      <EditarDocumentoDialog
        open={dialogEditarOpen}
        onOpenChange={setDialogEditarOpen}
        documento={documentoEditar}
        onSuccess={() => {
          setDocumentoEditar(null)
          refetch()
        }}
      />

      <DocumentoPreviewDialog
        documento={documentoPreview}
        open={dialogPreviewOpen}
        onOpenChange={(open) => {
          setDialogPreviewOpen(open)
          if (!open) setDocumentoPreview(null)
        }}
      />
    </div>
  )
}
